import { derived, get } from 'svelte/store';
import { erStatic } from '../static-all';
import { reduceToMap, reduceToArrayMap } from '$lib/utils/array';
import type ERAR from '$lib/erar/spec';
import type {
  BaseItemsBySource,
  EquipmentSubcategory,
  ItemIdCount,
  ItemIdToCountMap,
} from './types';
import { equipmentSubcategories, nonEquipmentSubcategories } from './consts';
import { immediateDerived } from '$lib/store';

// TODO: 옮기기
// 정렬 순서를 결정
export const gradeToNumber: Record<ERAR.Schema.Grade, number> = {
  C: 0,
  U: 1,
  R: 2,
  E: 3,
  L: 4,
  M: -1,
};

export const idToItem = immediateDerived(
  erStatic,
  (values) => {
    return reduceToMap(
      (values?.result?.items ?? []).filter((v) => v.lastVersion !== 'test' && v.id !== 409404),
      (v) => v.id,
      (v) => v,
    );
  },
  new Map<number, ERAR.Schema.StaticItemList[number]>(),
);

export const idToCrafts = immediateDerived(
  erStatic,
  (values) => {
    return reduceToArrayMap(
      values?.result.crafts ?? [],
      (v) => v.targetItemId,
      (v) => v,
    );
  },
  new Map<number, ERAR.Schema.StaticCraftList>(),
);

export const indexToSubcategory = immediateDerived(
  erStatic,
  (values) => {
    return reduceToMap(
      values?.result.itemSubcategories ?? [],
      (v) => v.index,
      (v) => v,
    );
  },
  new Map<number, ERAR.Schema.StaticItemSubcategoryList[number]>(),
);
export const idToSubcategory = immediateDerived(
  erStatic,
  (values) => {
    return reduceToMap(
      values?.result.itemSubcategories ?? [],
      (v) => v.id,
      (v) => v,
    );
  },
  new Map<string, ERAR.Schema.StaticItemSubcategoryList[number]>(),
);

export const idToCharacterWeapon = immediateDerived(
  erStatic,
  (values) => {
    return reduceToMap(
      values?.result.characterWeapons ?? [],
      (v) => v.id,
      (v) => v,
    );
  },
  new Map<number, ERAR.Schema.StaticCharacterWeaponList[number]>(),
);

export const itemAppearances = immediateDerived(
  erStatic,
  (values) => values?.result.itemAppearances ?? [],
  [] as ERAR.Schema.StaticItemAppearanceList,
);
export const monsterIdToItems = immediateDerived(
  itemAppearances,
  (values) => {
    return reduceToArrayMap(
      values.filter((itemAppearance) => itemAppearance.monsterId != undefined),
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      (v) => v.monsterId!,
      (v) => v,
    );
  },
  new Map<number, ERAR.Schema.StaticItemAppearanceList>(),
);
export const monsterAppearances = immediateDerived(
  erStatic,
  (values) => values?.result.monsterAppearances ?? [],
  [] as ERAR.Schema.StaticMonsterAppearanceList,
);

export const gatherableItems = immediateDerived(
  erStatic,
  (values) => {
    const nonUniqueGatherables = (values?.result.itemAppearances ?? [])
      .filter(
        (v) =>
          v.gatherability > 0 && v.areaId != undefined && v.group == null && v.monsterId == null,
      )
      .map((v) => v.itemId);
    return [...new Set(nonUniqueGatherables).values()];
  },
  [] as number[],
);

export const itemCategories = immediateDerived(
  erStatic,
  (values) => values?.result.itemCategories ?? [],
  [] as ERAR.Schema.StaticItemCategoryList,
);

export const categoryToSubcategories = immediateDerived(
  erStatic,
  (values) => {
    return reduceToArrayMap(
      values?.result.itemSubcategories ?? [],
      (v) => v.categoryId,
      (v) => v,
    );
  },
  new Map<string, ERAR.Schema.StaticItemSubcategoryList>(),
);

export const weaponSubcategories = immediateDerived(
  categoryToSubcategories,
  (values) => values.get('Weapon')?.map((v) => v.id) ?? [],
  [],
);

export const equippableItemSubcategories = immediateDerived(
  categoryToSubcategories,
  (values) =>
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    [...values.get('Weapon')!, ...values.get('Armor')!].map(
      (itemSubcategory) => itemSubcategory.id,
    ),
  [],
);

export const foods = immediateDerived(
  idToItem,
  (value) => [...value.values()].filter((item) => item.itemSubcategoryId === 'Food'),
  [],
);
export const beverages = immediateDerived(
  idToItem,
  (value) => [...value.values()].filter((item) => item.itemSubcategoryId === 'Beverage'),
  [],
);
export const summonObjects = immediateDerived(
  idToItem,
  (value) => [...value.values()].filter((item) => item.itemSubcategoryId === 'Summon'),
  [],
);
export const traps = immediateDerived(
  idToItem,
  (value) =>
    [...value.values()].filter(
      (item) => item.itemSubcategoryId === 'Summon' && (item.options?.trapPower ?? 0) > 0,
    ),
  [],
);
export const armorsBetterThanCommon = immediateDerived(
  idToItem,
  (value) => {
    return [...value.values()].filter(
      (item) =>
        gradeToNumber[item.gradeId] > gradeToNumber['C'] &&
        !nonEquipmentSubcategories.some((value) => value === item.itemSubcategoryId),
    );
  },
  [],
);

export const initialInventory = immediateDerived(
  erStatic,
  (values) =>
    Object.entries(values?.result.initialInventory ?? {}).map(([itemIdString, count]) => ({
      itemId: Number.parseInt(itemIdString, 10),
      count,
    })),
  [],
);

/**
 * 유틸 함수들
 */
export function findCharacterWeapon(
  characterId: number,
  weaponType: string,
): ERAR.Schema.StaticCharacterWeaponList[number] | undefined {
  let result;
  for (const characterWeapon of get(idToCharacterWeapon).values()) {
    if (
      characterWeapon.characterId === characterId &&
      characterWeapon.itemSubcategoryId === weaponType
    ) {
      result = characterWeapon;
    }
  }

  return result;
}

export function getCharacterWeaponsAvailable(
  characterId: number,
): ERAR.Schema.StaticCharacterWeaponList {
  return [...get(idToCharacterWeapon).values()].filter((cw) => cw.characterId === characterId);
}

export function getCharacterWeaponsNotAvailable(
  characterId: number,
): ERAR.Schema.StaticCharacterWeaponList | undefined {
  const weaponsAvailable = getCharacterWeaponsAvailable(characterId);

  if (weaponsAvailable.length === 0) return undefined;

  return [...get(idToCharacterWeapon).values()].filter(
    (cw) => !weaponsAvailable.find((weapon) => weapon.itemSubcategoryId === cw.itemSubcategoryId),
  );
}

export function getSubcategoryWithGeneralizedWeapon(itemSubcategoryId: string): string {
  return equipmentSubcategories.findIndex((value) => value === itemSubcategoryId) === -1
    ? 'Weapon'
    : itemSubcategoryId;
}

export function getEquipmentSubcategoryWithGeneralizedWeapon(
  itemSubcategoryId: string,
): EquipmentSubcategory {
  if (nonEquipmentSubcategories.some((value) => value === itemSubcategoryId)) {
    console.error('입력으로 장비가 들어오지 않았습니다:', itemSubcategoryId);
    return 'Weapon';
  }

  return equipmentSubcategories.findIndex((value) => value === itemSubcategoryId) === -1
    ? 'Weapon'
    : (itemSubcategoryId as EquipmentSubcategory);
}

export function getStartWeapon(
  itemSubcategoryId: string,
): ERAR.Schema.StaticItemList[number] | undefined {
  const weaponId = get(idToSubcategory).get(itemSubcategoryId)?.weaponId ?? -1;
  return get(idToItem).get(weaponId);
}

export function getBaseItemsBySource(itemIds: number[]): BaseItemsBySource {
  const baseItems: BaseItemsBySource = {
    search: [],
    gather: [],
  };

  for (const [itemId, count] of getBaseItems(itemIds).entries()) {
    if (get(gatherableItems).includes(itemId)) {
      baseItems.gather.push({ itemId, count });
    } else {
      baseItems.search.push({ itemId, count });
    }
  }

  return baseItems;
}

export function getBaseItems(itemIds: number[]): ItemIdToCountMap {
  const baseItems: ItemIdToCountMap = new Map();
  const surplusItems: ItemIdToCountMap = new Map();
  const queue: number[] = [];

  for (const itemId of itemIds) {
    queue.push(itemId);
  }

  while (queue.length > 0) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const itemId = queue.shift()!;
    const crafts = get(idToCrafts).get(itemId);

    if (surplusItems.has(itemId)) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const reducedCount = surplusItems.get(itemId)! - 1;

      if (reducedCount === 0) {
        surplusItems.delete(itemId);
      } else {
        surplusItems.set(itemId, reducedCount);
      }

      continue;
    }

    // 재료 아이템 정보가 없다면 자기가 재료
    if (crafts === undefined) {
      baseItems.set(itemId, (baseItems.get(itemId) ?? 0) + 1);
      continue;
    }

    for (const craft of crafts) {
      for (let i = 0; i < craft.quantity; i++) {
        queue.push(craft.sourceItemId);
      }
    }

    const initialCount = get(idToItem).get(itemId)?.initialCount ?? 0;

    if (initialCount > 1) {
      surplusItems.set(itemId, (surplusItems.get(itemId) ?? 0) + (initialCount - 1));
    }
  }

  return baseItems;
}

/**
 * 모든 하위 아이템을 구한다. 상위 아이템과 하위 아이템도 중복해서 구한다.
 * 예: 모카빵 -> 알코올 1, 커피콩 1, 커피 리큐르 1, 빵 1
 * @param goalItems 하위 아이템을 구할 아이템
 * @returns 모든 하위 아이템과 그 수
 */
export function getRequiredItemsWithDuplication(goalItems: ItemIdToCountMap): ItemIdToCountMap {
  const totalRequiredAmountsById: ItemIdToCountMap = new Map();

  const queue: { finalTargetItemId: number; id: number; requiredAmount: number }[] = [];
  for (const goalItemId of goalItems.keys()) {
    queue.push({
      finalTargetItemId: goalItemId,
      id: goalItemId,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      requiredAmount: goalItems.get(goalItemId)!,
    });
  }

  while (queue.length > 0) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const { finalTargetItemId, id, requiredAmount } = queue.shift()!;

    if (finalTargetItemId !== id) {
      totalRequiredAmountsById.set(id, (totalRequiredAmountsById.get(id) ?? 0) + requiredAmount);
    }

    const requiredItemCrafts = get(idToCrafts).get(id);
    if (!requiredItemCrafts) {
      continue;
    }

    for (const { sourceItemId, quantity } of requiredItemCrafts) {
      queue.push({
        finalTargetItemId,
        id: sourceItemId,
        requiredAmount: quantity * requiredAmount,
      });
    }
  }

  return totalRequiredAmountsById;
}

export function getAreaItems(areaId: number): ItemIdCount[] {
  const areaItemsOnlyByMonster: Set<number> = new Set();
  const itemsInArea = get(itemAppearances).filter((v) => v.areaId != null || v.gatherability > 0);

  for (const monsterAppearance of get(monsterAppearances)) {
    if (monsterAppearance.areaId === areaId) {
      for (const item of get(monsterIdToItems).get(monsterAppearance.monsterId) ?? []) {
        areaItemsOnlyByMonster.add(item.itemId);
      }
    }
  }

  for (const itemAppearance of itemsInArea) {
    areaItemsOnlyByMonster.delete(itemAppearance.itemId);
  }

  const monsterItems = [...areaItemsOnlyByMonster.values()].map<ItemIdCount>((itemId) => ({
    itemId,
    count: 1,
  }));
  const areaItemsWithDuplication = itemsInArea
    .filter((itemAppearance) => itemAppearance.areaId === areaId)
    .map<ItemIdCount>((itemAppearance) => ({
      itemId: itemAppearance.itemId,
      count: itemAppearance.gatherability,
    }));

  const areaItemCountsById = reduceToArrayMap(
    areaItemsWithDuplication,
    ({ itemId }) => itemId,
    ({ count }) => count,
  );

  const areaItems = [...areaItemCountsById.entries()].map(([itemId, counts]) => ({
    itemId,
    count: counts.reduce((sum, count) => sum + count, 0),
  }));

  return [...monsterItems, ...areaItems];
}

export function isWeapon(itemId: number) {
  return get(weaponSubcategories).includes(get(idToItem).get(itemId)?.itemSubcategoryId ?? '');
}
